import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/site";
import { toast } from "sonner";
import {
  LayoutDashboard, Cpu, Boxes, Sparkles, Mail, LogOut, Menu,
  TrendingUp, Activity, Gem, Zap, Brain, Plus, Pencil, Trash2, X, AlertTriangle, Lightbulb,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import ThemeToggle from "@/components/ThemeToggle";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";

const NAV = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "assets", label: "Model Assets", icon: Cpu },
  { key: "products", label: "Products", icon: Boxes },
  { key: "insights", label: "AI Insights", icon: Sparkles },
  { key: "contacts", label: "Inquiries", icon: Mail },
];

const EMPTY_ASSET = { name: "", family: "Luchii", tier: "General", params: "", context_window: "4K", status: "Live", description: "", compute_load: 45, inference_rps: 0, accuracy: 0, version: "v1.0" };
const EMPTY_PRODUCT = { name: "", category: "Platform", description: "", model_family: "Luchii", price: "", status: "Active" };

function Kpi({ icon: Icon, label, value, accent }) {
  return (
    <div className="rounded-2xl border border-emerald-900/60 surface-panel p-5 hover-emerald-glow" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-4 ${accent ? "border border-gold/40 bg-gold/5" : "border border-emerald-400/30 bg-emerald-500/10"}`}>
        <Icon className={`w-5 h-5 ${accent ? "text-gold" : "text-emerald-300"}`} />
      </div>
      <div className="font-display text-2xl text-emerald-50">{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function Overview({ data }) {
  if (!data) return <div className="text-slate-500">Loading intelligence…</div>;
  const { kpis, traffic, revenue } = data;
  return (
    <div className="space-y-8" data-testid="dashboard-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Kpi icon={Gem} label="Portfolio Valuation" value={kpis.valuation} accent />
        <Kpi icon={Activity} label="Active Neural Nodes" value={kpis.active_nodes} />
        <Kpi icon={Zap} label="Luchii Req / s" value={kpis.luchii_rps.toLocaleString()} />
        <Kpi icon={Brain} label="AI Insight Score" value={`${kpis.insight_score}`} accent />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-emerald-900/60 surface-panel p-6">
          <h3 className="font-garamond text-lg text-emerald-100 mb-1 tracking-wide">Daily Inference Traffic</h3>
          <p className="text-xs text-slate-500 mb-5">Luchii vs Frasberg request volume</p>
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={traffic}>
              <defs>
                <linearGradient id="gLuchii" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#059669" stopOpacity={0.5} /><stop offset="100%" stopColor="#059669" stopOpacity={0} /></linearGradient>
                <linearGradient id="gFras" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#0b3a26" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--solid)", border: "1px solid var(--line)", borderRadius: 12, color: "#fff" }} />
              <Area type="monotone" dataKey="luchii" stroke="#10b981" fill="url(#gLuchii)" strokeWidth={2} />
              <Area type="monotone" dataKey="frasberg" stroke="#D4AF37" fill="url(#gFras)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-emerald-900/60 surface-panel p-6">
          <h3 className="font-garamond text-lg text-emerald-100 mb-1 tracking-wide">Portfolio Revenue Growth</h3>
          <p className="text-xs text-slate-500 mb-5">Quarterly ($M)</p>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={revenue}>
              <CartesianGrid strokeDasharray="3 3" stroke="#0b3a26" />
              <XAxis dataKey="name" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "var(--solid)", border: "1px solid var(--line)", borderRadius: 12, color: "#fff" }} cursor={{ fill: "rgba(16,185,129,0.08)" }} />
              <Bar dataKey="value" fill="#059669" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

function AssetModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k, num) => (e) => setForm({ ...form, [k]: num ? Number(e.target.value) : e.target.value });
  const editing = !!initial.id;

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/assets/${initial.id}`, form);
      else await api.post("/admin/assets", form);
      toast.success(`Asset ${editing ? "updated" : "created"}.`);
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-emerald-800 surface-glass p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="asset-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-garamond text-xl text-emerald-50">{editing ? "Edit" : "New"} Model Asset</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-gold"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="asset-name" className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Family">
            <select value={form.family} onChange={set("family")} data-testid="asset-family" className="w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>Luchii</option><option>Frasberg</option>
            </select>
          </Field>
          <Field label="Tier"><Input value={form.tier} onChange={set("tier")} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Version"><Input value={form.version} onChange={set("version")} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Params"><Input value={form.params} onChange={set("params")} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Context"><Input value={form.context_window} onChange={set("context_window")} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")} className="w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>Live</option><option>Beta</option><option>Retired</option>
            </select>
          </Field>
          <Field label="Compute Load %"><Input type="number" value={form.compute_load} onChange={set("compute_load", true)} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Inference req/s"><Input type="number" value={form.inference_rps} onChange={set("inference_rps", true)} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Accuracy (MMLU)"><Input type="number" value={form.accuracy} onChange={set("accuracy", true)} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <div className="col-span-2"><Field label="Description"><Textarea rows={2} value={form.description} onChange={set("description")} className="surface-input border-emerald-900 text-emerald-50" /></Field></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="asset-save" className="w-full mt-5 bg-emerald-600 hover:bg-emerald-500 rounded-full h-11 border border-emerald-400/30">{saving ? "Saving…" : "Save Asset"}</Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div><label className="text-[11px] uppercase tracking-widest text-slate-400">{label}</label><div className="mt-1.5">{children}</div></div>;
}

function AssetsManager() {
  const [assets, setAssets] = useState([]);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => api.get("/assets").then(({ data }) => setAssets(data)), []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    await api.delete(`/admin/assets/${id}`);
    toast.success("Asset removed.");
    load();
  };

  return (
    <div data-testid="dashboard-assets">
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="font-garamond text-2xl text-emerald-50">Model Assets</h2><p className="text-sm text-slate-500">Manage the Luchii & Frasberg model families</p></div>
        <Button onClick={() => setModal(EMPTY_ASSET)} data-testid="add-asset-button" className="bg-gold text-emerald-950 hover:bg-gold/90 rounded-full h-10"><Plus className="w-4 h-4 mr-1.5" /> New Asset</Button>
      </div>
      <div className="rounded-2xl border border-emerald-900/60 surface-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="surface-input text-slate-400 text-[11px] uppercase tracking-widest">
              <tr>{["Model", "Family", "Tier", "Params", "Load", "Status", ""].map((h) => <th key={h} className="text-left px-4 py-3 font-medium">{h}</th>)}</tr>
            </thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id} className="border-t border-emerald-900/50 hover:bg-emerald-900/20" data-testid={`asset-row-${a.name.toLowerCase().replace(/\s/g, "-")}`}>
                  <td className="px-4 py-3 text-emerald-50 font-medium">{a.name}</td>
                  <td className="px-4 py-3"><Badge className={`border rounded-full text-[10px] ${a.family === "Luchii" ? "bg-emerald-500/10 text-emerald-300 border-emerald-500/30" : "bg-gold/10 text-gold border-gold/30"}`}>{a.family}</Badge></td>
                  <td className="px-4 py-3 text-slate-400">{a.tier}</td>
                  <td className="px-4 py-3 font-mono text-slate-300">{a.params}</td>
                  <td className="px-4 py-3 text-emerald-300">{a.compute_load}%</td>
                  <td className="px-4 py-3 text-slate-400">{a.status}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => setModal(a)} data-testid={`edit-asset-${a.name.toLowerCase().replace(/\s/g, "-")}`} className="text-slate-400 hover:text-gold mr-3"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => del(a.id)} data-testid={`delete-asset-${a.name.toLowerCase().replace(/\s/g, "-")}`} className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {modal && <AssetModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ProductModal({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const editing = !!initial.id;
  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/products/${initial.id}`, form);
      else await api.post("/admin/products", form);
      toast.success(`Product ${editing ? "updated" : "created"}.`);
      onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center px-4" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl border border-emerald-800 surface-glass p-6" onClick={(e) => e.stopPropagation()} data-testid="product-modal">
        <div className="flex justify-between items-center mb-5">
          <h3 className="font-garamond text-xl text-emerald-50">{editing ? "Edit" : "New"} Product</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-gold"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Name"><Input value={form.name} onChange={set("name")} data-testid="product-name" className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Category">
            <select value={form.category} onChange={set("category")} data-testid="product-category" className="w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>Platform</option><option>Studio</option><option>Enterprise</option>
            </select>
          </Field>
          <Field label="Model Family">
            <select value={form.model_family} onChange={set("model_family")} className="w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>Luchii</option><option>Frasberg</option>
            </select>
          </Field>
          <Field label="Price"><Input value={form.price} onChange={set("price")} className="surface-input border-emerald-900 text-emerald-50" /></Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")} className="w-full h-10 rounded-md surface-input border border-emerald-900 text-emerald-50 px-3 text-sm">
              <option>Active</option><option>Beta</option><option>Sunset</option>
            </select>
          </Field>
          <div className="col-span-2"><Field label="Description"><Textarea rows={2} value={form.description} onChange={set("description")} className="surface-input border-emerald-900 text-emerald-50" /></Field></div>
        </div>
        <Button onClick={save} disabled={saving} data-testid="product-save" className="w-full mt-5 bg-emerald-600 hover:bg-emerald-500 rounded-full h-11 border border-emerald-400/30">{saving ? "Saving…" : "Save Product"}</Button>
      </div>
    </div>
  );
}

function ProductsManager() {
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(null);
  const load = useCallback(() => api.get("/products").then(({ data }) => setProducts(data)), []);
  useEffect(() => { load(); }, [load]);
  const del = async (id) => { await api.delete(`/admin/products/${id}`); toast.success("Product removed."); load(); };

  return (
    <div data-testid="dashboard-products">
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="font-garamond text-2xl text-emerald-50">Products</h2><p className="text-sm text-slate-500">Holding products powered by the portfolio</p></div>
        <Button onClick={() => setModal(EMPTY_PRODUCT)} data-testid="add-product-button" className="bg-gold text-emerald-950 hover:bg-gold/90 rounded-full h-10"><Plus className="w-4 h-4 mr-1.5" /> New Product</Button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {products.map((p) => (
          <div key={p.id} className="rounded-2xl border border-emerald-900/60 surface-panel p-5 hover-emerald-glow" data-testid={`product-manage-${p.name.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex justify-between items-start">
              <Badge className="bg-transparent border border-emerald-800 text-slate-400 rounded-full text-[10px]">{p.category}</Badge>
              <div className="flex gap-2">
                <button onClick={() => setModal(p)} className="text-slate-400 hover:text-gold"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => del(p.id)} className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            <h3 className="text-emerald-50 font-garamond text-lg mt-3">{p.name}</h3>
            <p className="text-sm text-slate-400 mt-1">{p.description}</p>
            <div className="flex justify-between items-center mt-4 pt-3 border-t border-emerald-900/60 text-[11px]">
              <span className="text-gold font-mono">{p.price}</span><span className="text-emerald-300">{p.model_family} · {p.status}</span>
            </div>
          </div>
        ))}
      </div>
      {modal && <ProductModal initial={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function Insights() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const load = useCallback(() => api.get("/ai/insights").then(({ data }) => setReports(data)), []);
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setLoading(true);
    try {
      await api.post("/ai/insights", { topic: "portfolio" });
      toast.success("New AI intelligence report generated.");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="dashboard-insights">
      <div className="flex justify-between items-center mb-6">
        <div><h2 className="font-garamond text-2xl text-emerald-50">AI Business Insights</h2><p className="text-sm text-slate-500">Strategy reports generated by Luchii intelligence</p></div>
        <Button onClick={generate} disabled={loading} data-testid="generate-insight-button" className="bg-emerald-600 hover:bg-emerald-500 rounded-full h-10 border border-emerald-400/30">
          <Sparkles className="w-4 h-4 mr-1.5" /> {loading ? "Analyzing…" : "Generate Report"}
        </Button>
      </div>

      {reports.length === 0 && !loading && (
        <div className="rounded-2xl border border-dashed border-emerald-900/60 p-14 text-center text-slate-500" data-testid="insights-empty">
          <Brain className="w-10 h-10 mx-auto mb-3 text-emerald-700" />
          No reports yet. Generate your first intelligence report.
        </div>
      )}

      <div className="space-y-5">
        {reports.map((r) => (
          <div key={r.id} className="rounded-2xl border border-emerald-900/60 surface-panel p-6" data-testid="insight-report">
            <div className="flex justify-between items-start mb-4">
              <div>
                <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5" /> Intelligence Report · {r.topic}</div>
                <p className="text-slate-300 mt-2 max-w-3xl">{r.summary}</p>
              </div>
              <div className="text-center shrink-0 ml-4">
                <div className="font-display text-3xl gold-gradient-text">{r.health_score}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-500">Health</div>
              </div>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <InsightList icon={Lightbulb} title="Recommendations" items={r.recommendations} color="text-emerald-300" />
              <InsightList icon={TrendingUp} title="Opportunities" items={r.opportunities} color="text-gold" />
              <InsightList icon={AlertTriangle} title="Risks" items={r.risks} color="text-amber-400" />
            </div>
            <p className="text-sm text-slate-400 mt-4 pt-4 border-t border-emerald-900/60"><span className="text-emerald-400">Forecast · </span>{r.forecast}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InsightList({ icon: Icon, title, items, color }) {
  return (
    <div className="rounded-xl border border-emerald-900/50 surface-input p-4">
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-widest mb-2 ${color}`}><Icon className="w-3.5 h-3.5" /> {title}</div>
      <ul className="space-y-1.5">{(items || []).map((it, i) => <li key={i} className="text-sm text-slate-400 flex gap-2"><span className="text-emerald-600">›</span>{it}</li>)}</ul>
    </div>
  );
}

function Contacts() {
  const [contacts, setContacts] = useState([]);
  useEffect(() => { api.get("/admin/contacts").then(({ data }) => setContacts(data)); }, []);
  return (
    <div data-testid="dashboard-contacts">
      <h2 className="font-garamond text-2xl text-emerald-50 mb-1">Inquiries</h2>
      <p className="text-sm text-slate-500 mb-6">Institutional & enterprise contact submissions</p>
      {contacts.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-emerald-900/60 p-14 text-center text-slate-500">No inquiries yet.</div>
      ) : (
        <div className="space-y-3">
          {contacts.map((c) => (
            <div key={c.id} className="rounded-xl border border-emerald-900/60 surface-panel p-5" data-testid="contact-entry">
              <div className="flex justify-between items-start">
                <div><span className="text-emerald-50 font-medium">{c.name}</span> <span className="text-slate-500 text-sm">· {c.email}</span></div>
                <Badge className="bg-emerald-500/10 text-emerald-300 border border-emerald-500/30 rounded-full text-[10px]">{c.interest}</Badge>
              </div>
              {c.company && <div className="text-xs text-slate-500 mt-1">{c.company}</div>}
              <p className="text-sm text-slate-400 mt-2">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [active, setActive] = useState("overview");
  const [sidebar, setSidebar] = useState(false);
  const [overviewData, setOverviewData] = useState(null);

  useEffect(() => {
    if (active === "overview") api.get("/admin/dashboard").then(({ data }) => setOverviewData(data)).catch(() => {});
  }, [active]);

  const signOut = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex surface-page" data-testid="dashboard-page">
      <aside className={`fixed lg:static z-40 h-screen w-64 surface-solid border-r border-emerald-900/60 flex flex-col transition-transform ${sidebar ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="p-5 border-b border-emerald-900/60 flex items-center gap-3">
          <img src={COMPANY.logo} alt="crest" className="w-10 h-10 object-contain rounded-lg" />
          <div className="leading-tight"><div className="font-display text-xs tracking-widest text-emerald-50 uppercase">Jahzara Phenix</div><div className="text-[9px] tracking-[0.25em] text-gold uppercase">Command Center</div></div>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {NAV.map((n) => (
            <button
              key={n.key}
              onClick={() => { setActive(n.key); setSidebar(false); }}
              data-testid={`nav-${n.key}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all ${active === n.key ? "bg-emerald-600/20 text-emerald-100 border border-emerald-500/30" : "text-slate-400 hover:text-emerald-100 hover:bg-emerald-900/30 border border-transparent"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <div className="p-4 border-t border-emerald-900/60">
          <div className="flex items-center gap-3 mb-4 px-1">
            {user?.picture ? <img src={user.picture} alt="" className="w-9 h-9 rounded-full" /> : <div className="w-9 h-9 rounded-full bg-emerald-600/30 flex items-center justify-center text-emerald-200 text-sm">{user?.name?.[0] || "A"}</div>}
            <div className="leading-tight overflow-hidden"><div className="text-sm text-emerald-50 truncate">{user?.name}</div><div className="text-[10px] text-gold uppercase tracking-widest">{user?.role}</div></div>
          </div>
          <Button onClick={signOut} data-testid="sign-out-button" className="w-full bg-transparent border border-emerald-900 text-slate-300 hover:text-red-400 hover:border-red-900/50 rounded-full h-10"><LogOut className="w-4 h-4 mr-2" /> Sign Out</Button>
        </div>
      </aside>

      {sidebar && <div className="fixed inset-0 bg-black/60 z-30 lg:hidden" onClick={() => setSidebar(false)} />}

      <main className="flex-1 h-screen overflow-y-auto">
        <header className="surface-glass border-b border-emerald-900/60 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-emerald-100" onClick={() => setSidebar(true)} data-testid="sidebar-toggle"><Menu /></button>
            <div><h1 className="font-display text-lg text-emerald-50 uppercase tracking-wide">{NAV.find((n) => n.key === active)?.label}</h1></div>
          </div>
          <div className="flex items-center gap-3 text-xs text-emerald-400"><ThemeToggle /><span className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot" /> System Nominal</div>
        </header>

        <div className="p-6 lg:p-8">
          {active === "overview" && <Overview data={overviewData} />}
          {active === "assets" && <AssetsManager />}
          {active === "products" && <ProductsManager />}
          {active === "insights" && <Insights />}
          {active === "contacts" && <Contacts />}
        </div>
      </main>
    </div>
  );
}
